import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Thumb, SelectionBadge, ProcurementBadge, ClientStatusBadge, EmptyState } from "@/components/Bits";
import { money, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProductBoard({ products, addLink, emptyTitle = "No selections yet", compact }) {
  const { isStaff } = useAuth();
  if (!products?.length) {
    return (
      <EmptyState
        testId="product-board-empty"
        title={emptyTitle}
        body={
          isStaff
            ? "Add products manually or from a retailer link to start building the scheme."
            : "Your designer is curating this space — selections will appear here as they’re ready."
        }
        action={
          addLink && isStaff && (
            <Link to={addLink} className="mt-2 inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Product
            </Link>
          )
        }
      />
    );
  }
  return (
    <div className={`grid gap-6 ${compact ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`} data-testid="product-board">
      {products.map((p, i) => (
        <Link
          key={p.id}
          to={`/products/${p.id}`}
          data-testid={`product-card-${p.id}`}
          className="card-surface card-hover group animate-rise overflow-hidden"
          style={{ animationDelay: `${i * 40}ms` }}
        >
          <div className="aspect-square overflow-hidden bg-line">
            {productImage(p) ? (
              <img src={productImage(p)} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
            ) : (
              <Thumb className="h-full w-full" rounded="rounded-none" />
            )}
          </div>
          <div className="space-y-3 p-5">
            <div className="overline">{p.category}</div>
            <h3 className="line-clamp-2 font-serif text-lg leading-snug text-ink">{p.name}</h3>
            <div className="flex items-baseline justify-between gap-2">
              <span className="num text-sm text-ink">{money(p.client_total ?? p.client_unit_price)}</span>
              {p.quantity > 1 && <span className="num text-xs text-fog">×{p.quantity}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {isStaff ? (
                <>
                  <SelectionBadge status={p.selection_status} testId={`product-selection-${p.id}`} />
                  <ProcurementBadge status={p.procurement_status} />
                </>
              ) : (
                <ClientStatusBadge status={p.client_status} testId={`product-client-status-${p.id}`} />
              )}
            </div>
            {p.vendor_name && <div className="text-xs text-fog">{p.vendor_name}</div>}
          </div>
        </Link>
      ))}
    </div>
  );
}
